import {
  useState,
  useEffect,
  useMemo,
  memo,
  Fragment,
} from 'react';
import classNames from 'classnames/bind';
import { useInView } from 'react-intersection-observer';

import Slider from '../../../../Shared/ReactSlick';
import CFLink from '../../../../Shared/CFLink';
import DcscPoweredByWithTooltip from '../../../../Shared/DcscPoweredByWithTooltip';

import withComponentName from '../../../../../decorators/withComponentName';

import { convertPermalinkToPath } from '../../../../../helpers/urlHelpers';

import useSizes from '../../../../../hooks/sizes';

import Styles from '../styles.module.scss';

import ButtonComponent from '../../../../Buttons/ButtonComponent';
import HPService from '../../../../../services/HPService';
import { NextArrow, PrevArrow } from '../../../../CustomCarousel/CustomCarouselControls';

const cx = classNames.bind(Styles);

export const NewsCoverage = ({ sendHomePageActions }) => {
  const [newsCoverageData, setNewsCoverageData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const { ref, inView } = useInView({
    skip: Boolean(newsCoverageData),
  });
  const { width } = useSizes();

  useEffect(() => {
    let mounted = true;

    if (inView) {
      HPService.getCarouselData('news_coverages').then((data) => {
        if (!mounted || !data) return;

        setNewsCoverageData({
          main: data.filter((item) => item.category === 'main'),
          assetClasses: data.filter((item) => item.category === 'asset_classes'),
          otherTopics: data.filter((item) => item.category === 'other_topics'),
          sectors: data
            .filter(({ category, order }) => category === 'sectors' && order !== 1 && order !== 2 && order !== 3)
            .map((obj) => ({
              ...obj,
              name: obj.order === 4 ? '' : obj.name,
              url: 'https://www.dcsc.ai/sectors',
            })),
          locations: data
            .filter(({ category, order }) => category === 'locations' && order !== 4 && order !== 5),
        });
        setLoaded(true);
      });
    }

    return () => {
      mounted = false;
    };
  }, [inView]);

  const settings = useMemo(() => ({
    rows: 1,
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    appendDots: (dots) => (
      <div>
        <ul className="dots">{dots}</ul>
      </div>
    ),
  }), []);

  const renderCoverageComponent = () => {
    if (width > 1024) {
      return (
        <>
          <div className="mr-2 ml-2">
            <AssetClasses
              assetClasses={newsCoverageData.assetClasses}
            />
          </div>
          <div className="mr-2 ml-2">
            <OtherTopics
              otherTopics={newsCoverageData.otherTopics}
            />
          </div>
          <div className="mr-2 ml-2">
            <SectorsLocations
              sectors={newsCoverageData.sectors}
              locations={newsCoverageData.locations}
            />
          </div>
        </>
      );
    }
    if (width > 767) {
      return (
        <Slider {...settings}>
          <div className="mr-2 ml-2">
            <div className={Styles.news_coverage_tab_holder}>
              <AssetClasses
                assetClasses={newsCoverageData.assetClasses}
              />
              <OtherTopics
                otherTopics={newsCoverageData.otherTopics}
              />
            </div>
          </div>
          <div className="mr-2 ml-2">
            <SectorsLocations
              sectors={newsCoverageData.sectors}
              locations={newsCoverageData.locations}
            />
          </div>
        </Slider>
      );
    }
    return (
      <Slider {...settings}>
        <div>
          <AssetClasses
            assetClasses={newsCoverageData.assetClasses}
          />
        </div>
        <div>
          <OtherTopics
            otherTopics={newsCoverageData.otherTopics}
          />
        </div>
        <div>
          <SectorsLocations
            sectors={newsCoverageData.sectors}
            locations={newsCoverageData.locations}
          />
        </div>
      </Slider>
    );
  };

  return (
    <section ref={ref} className={Styles.news_coverage}>
      <h3 className={`${Styles.title} text-uppercase`}>
        TRACK 300,000+ SECURITIES AND TOPICS
      </h3>
      {loaded && (
        <>
          <div className={Styles['general-numbers']}>
            {newsCoverageData.main.map((mainCategory) => (
              <Fragment key={mainCategory.name}>
                {mainCategory.name === 'Languages' ? (
                  <a
                    className={Styles['general-number-item']}
                    key={mainCategory.name}
                    href="https://www.cityfalcon.ai/features/language-coverage"
                  >
                    <span>{mainCategory.coverage}</span>
                    <p>{mainCategory.name}</p>
                  </a>
                ) : (
                  <div
                    className={Styles['general-number-item']}
                    key={mainCategory.name}
                  >
                    <span>{mainCategory.coverage}</span>
                    <p>{mainCategory.name}</p>
                  </div>
                )}
              </Fragment>
            ))}
          </div>
          <div className={Styles.news_coverage_list}>
            {renderCoverageComponent()}
          </div>
        </>
      )}
      <div className={Styles.button_container}>
        <ButtonComponent
          checkEvent={() => sendHomePageActions && sendHomePageActions('See Full Coverage')}
          path="/news/directory"
          value="See Full Coverage"
          longer
        />
      </div>
    </section>
  );
};

const AssetClasses = ({ assetClasses }) => (
  <NewsCoveragesList
    list={assetClasses}
    listTitle="Asset Classes"
    twoColumnList
  />
);

const OtherTopics = ({ otherTopics }) => (
  <NewsCoveragesList
    list={otherTopics}
    listTitle="other topics"
    twoColumnList
  />
);

const SectorsLocations = ({ sectors, locations }) => (
  <>
    <NewsCoveragesList
      list={sectors}
      listTitle="sectors"
    />
    <NewsCoveragesList
      list={locations}
      listTitle="locations"
    />
  </>
);

const NewsCoveragesList = ({ list, listTitle, twoColumnList }) => (
  <div className={cx('news_coverage_column', { [listTitle]: true })}>
    <h3>{listTitle}</h3>
    <ul className={cx('simple-list', { 'two-column-list': twoColumnList })}>
      {list.map((listItem) => (
        <li key={listItem.name}>
          <CFLink
            external={listItem.url?.includes('dcsc.ai')}
            to={(
              listItem.url && !listItem.url?.includes('dcsc.ai')
                ? convertPermalinkToPath(listItem.url)
                : listItem.url
            )}
            className={cx('seo-link', { 'present-link': Boolean(listItem.url) })}
          >
            <span>{listItem.coverage}</span>
            <p>
              {listItem.name}
            </p>
          </CFLink>
          {listItem.url?.includes('dcsc.ai') && (
            <DcscPoweredByWithTooltip
              className={Styles.dcsc}
              url="https://dcsc.ai/sectors"
            />
          )}
        </li>
      ))}
    </ul>
  </div>
);

export default withComponentName(memo(NewsCoverage));
